import React, { useState, useRef, useEffect } from "react";
import { Modal, IconButton, Box } from "@mui/material";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";
import ArgonSelect from "components/ArgonSelect";
import userProfile from "assets/images/user-profile.png";
import EditIcon from "@mui/icons-material/Edit";
import { addBusinessUser, editBusinessUser } from "services/usersServices";
import { CATEGORIES, SUBSCRIPTIONS } from "../consts";
import { toast } from "react-toastify";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  minWidth: 750,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "16px",
  py: 5,
  px: 15,
};

function BusinessUserModal({ open, handleClose, user, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const isEdit = Boolean(user);

  useEffect(() => {
    if (user) {
      setForm({
        businessName: user.businessName || "",
        name: user.name || "",
        category: user.category
          ? { value: user.category, label: user.category }
          : null,
        email: user.email || "",
        phone: user.phone || "",
        subscription: user.subscription
          ? { value: user.subscription, label: user.subscription }
          : null,
        address: user.address || "",
        notes: user.notes || "",
        avatar: user.avatar || userProfile,
      });
      setAvatarPreview(user.avatar || userProfile); // Update avatar preview as well
    }
  }, [user]); // This effect runs whenever the user prop changes;
  const [form, setForm] = useState({
    businessName: user?.businessName || "",
    name: user?.name || "",
    category: user?.category
      ? { value: user.category, label: user.category }
      : null,
    email: user?.email || "",
    phone: user?.phone || "",
    subscription: user?.subscription
      ? { value: user.subscription, label: user.subscription }
      : null,
    address: user?.address || "",
    notes: user?.notes || "",
    avatar: user?.avatar || userProfile,
  });
  const [avatarPreview, setAvatarPreview] = useState(form.avatar);
  const fileInputRef = useRef();

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    handleChange(name, value);
  };
  const handleAvatarClick = () => {
    fileInputRef.current.click();
  };
  const handleAvatarChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        setAvatarPreview(ev.target.result);
        handleChange("avatar", ev.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  const handleSelectChange = (field) => (option) => {
    handleChange(field, option);
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.category || !form.subscription) {
      toast.error("Email, Name, Category and Subscription are required fields");
      return;
    }
    setLoading(true);
    const payload = new FormData();
    payload.append("businessName", form.businessName);
    payload.append("name", form.name);
    payload.append("category", form.category?.value || "");
    payload.append("email", form.email);
    payload.append("phone", form.phone);
    payload.append("businessSubscriptionType", form.subscription?.value || "");
    payload.append("address", form.address);
    payload.append("notes", form.notes);

    // If a file is selected, append it
    const file = fileInputRef.current?.files[0];
    if (file) {
      payload.append("avatar", file);
    }

    try {
      if (isEdit) {
        await editBusinessUser(user._id, payload);
      } else {
        await addBusinessUser(payload);
      }
      onUpdate();
      setLoading(false);
      handleClose();
    } catch (err) {
      console.error("Error submitting form:", err);
      toast.error(err.response.data.message);
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={handleClose}>
      <Box sx={style}>
        <form onSubmit={handleSubmit}>
          <ArgonBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={3}
          >
            <ArgonTypography variant="h4" fontWeight="medium" mb={2}>
              {isEdit ? "Edit Business User" : "Add Business User"}
            </ArgonTypography>
            <Box position="relative" mb={2}>
              <img
                src={avatarPreview}
                alt="avatar"
                style={{
                  width: 100,
                  height: 100,
                  objectFit: "cover",
                  borderRadius: "12px", // Rounded corners for the image
                }}
              />
              <IconButton
                onClick={handleAvatarClick}
                sx={{
                  position: "absolute",
                  bottom: -10,
                  right: -10,
                  bgcolor: "#2d3f50",
                  border: "1px solid #2d3f50",
                  borderRadius: "12px",
                  width: 32,
                  height: 32,
                }}
              >
                <EditIcon fontSize="small" color="white" />
              </IconButton>
              <input
                type="file"
                accept="image/jpeg, image/png, image/jpg"
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleAvatarChange}
              />
            </Box>
          </ArgonBox>
          <ArgonBox display="flex" gap={2} mb={2}>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Business Name
              </ArgonTypography>
              <ArgonInput
                name="businessName"
                value={form.businessName}
                onChange={handleInputChange}
                fullWidth
              />
            </ArgonBox>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Contact Person Name
              </ArgonTypography>
              <ArgonInput
                name="name"
                value={form.name}
                onChange={handleInputChange}
                fullWidth
              />
            </ArgonBox>
          </ArgonBox>
          <ArgonBox display="flex" gap={2} mb={2}>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Category
              </ArgonTypography>
              <ArgonSelect
                value={form.category}
                options={CATEGORIES}
                onChange={handleSelectChange("category")}
                placeholder="Select Category"
              />
            </ArgonBox>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Email
              </ArgonTypography>
              <ArgonInput
                name="email"
                type="email"
                value={form.email}
                onChange={handleInputChange}
                fullWidth
              />
            </ArgonBox>
          </ArgonBox>
          <ArgonBox display="flex" gap={2} mb={2}>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Phone Number
              </ArgonTypography>
              <ArgonInput
                name="phone"
                value={form.phone}
                onChange={handleInputChange}
                fullWidth
              />
            </ArgonBox>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Subscription Plan
              </ArgonTypography>
              <ArgonSelect
                value={form.subscription}
                options={SUBSCRIPTIONS}
                onChange={handleSelectChange("subscription")}
                placeholder="Select Subscription"
              />
            </ArgonBox>
          </ArgonBox>
          <ArgonBox display="flex" gap={2} mb={2}>
            <ArgonBox flex={1}>
              <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
                Address
              </ArgonTypography>
              <ArgonInput
                name="address"
                value={form.address}
                onChange={handleInputChange}
                fullWidth
              />
            </ArgonBox>
          </ArgonBox>
          <ArgonBox mb={2}>
            <ArgonTypography variant="h6" fontWeight="bold" mb={1}>
              Notes
            </ArgonTypography>
            <ArgonInput
              name="notes"
              value={form.notes}
              onChange={handleInputChange}
              fullWidth
              multiline
              rows={3}
            />
          </ArgonBox>
          <ArgonBox display="flex" justifyContent="center" gap={2} mt={3}>
            <ArgonButton
              variant="outlined"
              color="primary"
              onClick={handleClose}
              type="button"
            >
              Cancel
            </ArgonButton>
            <ArgonButton
              variant="contained"
              color="primary"
              type="submit"
              disabled={loading}
            >
              {loading ? "Loading..." : isEdit ? "Update" : "Add"}
            </ArgonButton>
          </ArgonBox>
        </form>
      </Box>
    </Modal>
  );
}

export default BusinessUserModal;
